import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Users,
  QrCode,
  Sticker,
  ScanLine,
  DollarSign,
  X,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { TUTORIAL_STEPS, TUTORIAL_LS_KEY } from "@/lib/tutorial";
import { Button } from "@/components/ui/button";

const ICON_MAP = { Building2, Users, QrCode, Sticker, ScanLine, DollarSign };

/**
 * TutorialOverlay — in-app coach marks.
 *
 * Behaviour:
 *   • Rendered at App level (outside <Routes>) so it persists across nav.
 *   • Visibility is controlled via a global event bus (window events) so any
 *     page can open it without prop drilling:
 *        window.dispatchEvent(new CustomEvent("scanmint:tutorial:open", { detail: { step: 0 } }))
 *   • On "Next" at the last step OR "Skip tutorial", we POST /users/me/tutorial/complete
 *     and set localStorage so the overlay doesn't re-appear on the next mount.
 *   • If an anchor data-testid is found on the current page, we ring it.
 *     Otherwise the card is centered — no broken positioning.
 */
export default function TutorialOverlay() {
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [anchorRect, setAnchorRect] = useState(null);
  const cardRef = useRef(null);
  const step = TUTORIAL_STEPS[stepIdx];
  const total = TUTORIAL_STEPS.length;
  const Icon = ICON_MAP[step?.icon] || Building2;

  // Open handler (listens for the global event)
  useEffect(() => {
    const h = (e) => {
      const at = e?.detail?.step ?? 0;
      setStepIdx(Math.max(0, Math.min(TUTORIAL_STEPS.length - 1, at)));
      setOpen(true);
    };
    window.addEventListener("scanmint:tutorial:open", h);
    return () => window.removeEventListener("scanmint:tutorial:open", h);
  }, []);

  // Find anchor element position for the current step
  useEffect(() => {
    if (!open || !step?.anchor) {
      setAnchorRect(null);
      return;
    }
    const find = () => {
      const el = document.querySelector(`[data-testid="${step.anchor}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setAnchorRect({
          top: r.top + window.scrollY,
          left: r.left + window.scrollX,
          width: r.width,
          height: r.height,
        });
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
      } else {
        setAnchorRect(null);
      }
    };
    // try now + on resize/scroll
    find();
    window.addEventListener("resize", find);
    window.addEventListener("scroll", find, true);
    return () => {
      window.removeEventListener("resize", find);
      window.removeEventListener("scroll", find, true);
    };
  }, [open, stepIdx, step]);

  const dismiss = async (completed) => {
    setOpen(false);
    setStepIdx(0);
    try {
      localStorage.setItem(TUTORIAL_LS_KEY, completed ? "completed" : "skipped");
    } catch {}
    try {
      await api.post("/users/me/tutorial/complete");
    } catch {
      // best-effort — the LS flag still prevents re-opening on this device
    }
  };

  const next = () => {
    if (stepIdx >= total - 1) dismiss(true);
    else setStepIdx(stepIdx + 1);
  };
  const prev = () => setStepIdx(Math.max(0, stepIdx - 1));

  // Card position: when we have an anchor, sit below or above it on wide
  // screens; on mobile just anchor to the bottom of the screen for
  // predictable one-handed use.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const cardStyle = useMemo(() => {
    if (isMobile || !anchorRect) return {};
    const CARD_W = 360;
    const CARD_H = 230;
    const pad = 12;
    const vw = window.innerWidth;
    const belowTop = anchorRect.top + anchorRect.height + pad;
    const spaceBelow = window.innerHeight - (anchorRect.top + anchorRect.height);
    const placeAbove = spaceBelow < CARD_H + 40;
    const top = placeAbove ? anchorRect.top - CARD_H - pad + window.scrollY : belowTop;
    let left = anchorRect.left + anchorRect.width / 2 - CARD_W / 2;
    left = Math.max(pad, Math.min(vw - CARD_W - pad, left));
    return { position: "absolute", top, left, width: CARD_W };
  }, [anchorRect, isMobile]);

  if (!open || !step) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] pointer-events-none"
        data-testid="tutorial-overlay"
      >
        {/* Dark backdrop with hole punched out around the anchor */}
        <div
          className="absolute inset-0 bg-black/60 pointer-events-auto"
          onClick={() => { /* swallow clicks so user can't skip accidentally */ }}
        />
        {/* Spotlight ring around the anchored element */}
        {anchorRect && (
          <motion.div
            layout
            className="absolute rounded-lg pointer-events-none"
            style={{
              top: anchorRect.top - 6,
              left: anchorRect.left - 6,
              width: anchorRect.width + 12,
              height: anchorRect.height + 12,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.6), 0 0 0 3px #0F8F4F, 0 0 24px 6px rgba(15, 143, 79, 0.5)",
            }}
            data-testid="tutorial-spotlight"
          />
        )}

        {/* Card */}
        <motion.div
          ref={cardRef}
          key={stepIdx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.25 }}
          className={
            isMobile || !anchorRect
              ? "pointer-events-auto fixed inset-x-4 bottom-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[380px] bg-white rounded-xl shadow-2xl border border-[#E4E4E7] overflow-hidden"
              : "pointer-events-auto bg-white rounded-xl shadow-2xl border border-[#E4E4E7] overflow-hidden"
          }
          style={isMobile ? undefined : cardStyle}
          data-testid="tutorial-card"
        >
          <div className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="w-10 h-10 rounded-md bg-[#0F8F4F]/10 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
              </div>
              <button
                type="button"
                onClick={() => dismiss(false)}
                className="text-[#71717A] hover:text-[#09090B] p-1 -m-1"
                aria-label="Skip tutorial"
                data-testid="tutorial-skip"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mono-tiny text-[#71717A] mt-3" data-testid="tutorial-step-indicator">
              Step {stepIdx + 1} of {total}
            </div>
            <div className="font-display text-lg font-bold mt-1" data-testid="tutorial-title">
              {step.title}
            </div>
            <p className="text-sm text-[#3F3F46] mt-2 leading-relaxed" data-testid="tutorial-body">
              {step.body}
            </p>
            {/* progress bar */}
            <div className="mt-4 h-1 bg-[#F4F4F5] rounded-full overflow-hidden">
              <motion.div
                layout
                className="h-full bg-[#0F8F4F] rounded-full"
                style={{ width: `${((stepIdx + 1) / total) * 100}%` }}
              />
            </div>
          </div>
          <div className="bg-[#FAFAFA] border-t border-[#E4E4E7] px-5 py-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => dismiss(false)}
              className="text-xs font-semibold text-[#71717A] hover:text-[#09090B]"
              data-testid="tutorial-skip-text"
            >
              Skip tutorial
            </button>
            <div className="flex gap-2">
              {stepIdx > 0 && (
                <Button size="sm" variant="outline" onClick={prev} data-testid="tutorial-prev">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={next}
                className="bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                data-testid="tutorial-next"
              >
                {stepIdx >= total - 1 ? (
                  <>Done <CheckCircle2 className="w-3.5 h-3.5 ml-1" /></>
                ) : (
                  <>Next <ArrowRight className="w-3.5 h-3.5 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/** Helper to programmatically open the tutorial at a given step. */
export function openTutorial(step = 0) {
  window.dispatchEvent(
    new CustomEvent("scanmint:tutorial:open", { detail: { step } })
  );
}
